// frontend/src/lib/breadcrumbs.ts
//
// Breadcrumbs for the floating header. Pure, so it is tested without React.
//
// Every trail is rooted at "Command Briefing". A known page (lib/naming.ts)
// is one crumb. Below a known page, the last segment is one more crumb: the
// short title the page set (for a record, "Hospital beds"), or until then the
// segment itself (for a record, its reference). Every crumb but the last is a
// link.
//
// No trail depends on the query any more: the context graph is a section under
// every Coworker Hub tab (plan 08), so `/agent-hub` with any query reads
// "Command Briefing › Coworker Hub".

import { HOME_CRUMB, PAGE_NAMES } from './naming'

export interface Crumb {
  label: string
  /** Null for the last crumb: the page you are on is not a link. */
  href: string | null
}

/** "supply-chain" becomes "Supply chain"; a reference with digits is kept as is. */
function humanize(segment: string): string {
  if (/\d/.test(segment)) return segment
  const words = segment.replace(/[-_]+/g, ' ').trim()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

function safeDecode(segment: string): string {
  try {
    return decodeURIComponent(segment)
  } catch {
    return segment
  }
}

export function buildBreadcrumbs(
  pathname: string,
  pageTitle?: string | null,
  /** The query string, with or without the leading "?". */
  search?: string | null
): Crumb[] {
  const clean = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname
  if (clean === '/' || clean === '') return [{ label: HOME_CRUMB, href: null }]

  // Kept in the signature for the callers; no trail reads it (plan 08).
  void search

  const segments = clean.split('/').filter(Boolean)

  // The longest prefix of the route that is a known page.
  let knownCount = 0
  let known: { path: string; name: string } | null = null
  for (let count = segments.length; count >= 1; count -= 1) {
    const path = `/${segments.slice(0, count).join('/')}`
    const page = PAGE_NAMES[path]
    if (page) {
      known = { path, name: page.name }
      knownCount = count
      break
    }
  }

  const crumbs: Crumb[] = [{ label: HOME_CRUMB, href: '/' }]
  const hasTail = knownCount < segments.length

  if (known) {
    crumbs.push({ label: known.name, href: hasTail ? known.path : null })
  }
  if (hasTail) {
    const tail = safeDecode(segments[segments.length - 1])
    crumbs.push({ label: pageTitle?.trim() || humanize(tail), href: null })
  }
  return crumbs
}
